import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import { Project } from "models/Project";
import { Tenant } from "models/Tenant";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Card, CardBody, Spinner, Table } from "reactstrap";
import { addInvoicePayment } from "services/Api";
import {
    buildSepaPaymentFile,
    buildWeeklyProjectPayoutRows,
    dateKey,
    invoiceNameMatchesWeek,
    isoWeekFor,
    isValidIban,
    mondayFor,
    normalizeIban,
    shiftDays,
} from "./projectPayouts";

interface Props {
    projects: Project[];
    accounts: Account[];
    invoices: Invoice[];
    tenants: Tenant[];
    onInvoiceUpdated: (invoice: Invoice) => void;
}

const ProjectPayoutOverview: React.FC<Props> = ({ projects, accounts, invoices, tenants, onInvoiceUpdated }) => {
    const { intlLocale, t } = useOfficeTranslation();
    const today = useMemo(() => new Date(), []);
    const [weekStart, setWeekStart] = useState(() => shiftDays(mondayFor(today), -7));
    const [downloadError, setDownloadError] = useState("");
    const [paymentError, setPaymentError] = useState("");
    const [markingPaid, setMarkingPaid] = useState(false);
    const [excludedPaymentIds, setExcludedPaymentIds] = useState<Set<string>>(() => new Set());
    const rows = useMemo(
        () => buildWeeklyProjectPayoutRows(projects, accounts, invoices, weekStart),
        [projects, accounts, invoices, weekStart],
    );
    const week = isoWeekFor(weekStart);
    const weekEnd = shiftDays(weekStart, 6);
    const payoutDate = shiftDays(weekStart, 8);
    const pendingHours = rows.reduce((total, row) => total + row.pendingHours, 0);
    const payableRows = rows.filter(row => !!row.workerInvoice && !row.alreadyPaid);
    const selectedPayableRows = payableRows.filter(row => !excludedPaymentIds.has(row.workerInvoice!.id));
    const allPaymentsSelected = payableRows.length > 0 && selectedPayableRows.length === payableRows.length;
    const displayRows = rows.filter(row => !!row.workerInvoice || row.pendingHours > 0);
    const mismatchedWeekInvoices = Array.from(new Map(
        rows.flatMap(row => [row.workerInvoice, row.platformFeeInvoice])
            .filter((invoice): invoice is Invoice => !!invoice && !invoiceNameMatchesWeek(invoice, weekStart))
            .map(invoice => [invoice.id, invoice]),
    ).values());
    const workersWithoutIban = Array.from(new Map(
        displayRows
            .filter(row => !normalizeIban(row.bankAccount?.iban))
            .map(row => [row.workerId, row]),
    ).values());
    const workersWithInvalidIban = Array.from(new Map(
        displayRows
            .filter(row => {
                const iban = normalizeIban(row.bankAccount?.iban);
                return !!iban && !isValidIban(iban);
            })
            .map(row => [row.workerId, row]),
    ).values());
    const selectedPendingHours = selectedPayableRows.reduce((total, row) => total + row.pendingHours, 0);
    const missingPayableIbans = selectedPayableRows.filter(row => !isValidIban(row.bankAccount?.iban));
    const draftRows = selectedPayableRows.filter(row => row.hasDraftInvoice);
    const missingFeeRows = selectedPayableRows.filter(row => row.missingPlatformFeeInvoice);
    const invalidAmountRows = selectedPayableRows.filter(row => row.net <= 0);
    const nonEuroRows = selectedPayableRows.filter(row => row.currency.toUpperCase() !== "EUR");
    const payableTenantIds = Array.from(new Set(selectedPayableRows.map(row => row.tenantId)));
    const multipleTenants = payableTenantIds.length > 1;
    const paymentTenant = payableTenantIds.length === 1
        ? tenants.find(tenant => tenant.id === payableTenantIds[0])
        : undefined;
    const tenantIbanValid = !!paymentTenant && isValidIban(paymentTenant.billing?.iban_account);
    const markPaidBlocked = multipleTenants || selectedPendingHours > 0 ||
        draftRows.length > 0 || missingFeeRows.length > 0 || invalidAmountRows.length > 0 ||
        nonEuroRows.length > 0 || selectedPayableRows.length === 0;
    const paymentFileBlocked = markPaidBlocked || !tenantIbanValid || missingPayableIbans.length > 0;
    const total = selectedPayableRows
        .filter(row => row.currency.toUpperCase() === "EUR")
        .reduce((sum, row) => sum + Math.max(0, row.net), 0);
    const currency = (amount: number, code = "EUR") =>
        new Intl.NumberFormat(intlLocale, { style: "currency", currency: code }).format(amount);

    const download = () => {
        try {
            const paymentFile = buildSepaPaymentFile({
                weekStart,
                tenantName: paymentTenant?.name ?? t("common.tenant"),
                tenantIban: paymentTenant?.billing?.iban_account ?? "",
                rows: selectedPayableRows,
            });
            const url = URL.createObjectURL(new Blob([paymentFile.xml], { type: "application/xml;charset=utf-8" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = paymentFile.filename;
            link.click();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            setDownloadError("");
        } catch (reason: any) {
            setDownloadError(reason.message ?? t("projects.projectPayoutOverview.downloadFailed"));
        }
    };

    const toggleAllPayments = () => {
        setExcludedPaymentIds(current => {
            if (!payableRows.length) return current;
            if (payableRows.every(row => !current.has(row.workerInvoice!.id))) {
                return new Set(Array.from(current).concat(
                    payableRows.map(row => row.workerInvoice!.id),
                ));
            }
            const next = new Set(current);
            payableRows.forEach(row => next.delete(row.workerInvoice!.id));
            return next;
        });
    };

    const markAsPaid = async () => {
        if (markPaidBlocked || markingPaid) return;

        setMarkingPaid(true);
        setPaymentError("");
        const date = dateKey(new Date());
        try {
            for (const row of selectedPayableRows) {
                // Keep the worker invoice selectable until both payments have been saved.
                for (const invoice of [row.platformFeeInvoice!, row.workerInvoice!]) {
                    if (invoice.IsPaid()) continue;
                    const updatedInvoice = await addInvoicePayment(invoice.id, {
                        date,
                        amount: Number(invoice.CalcPendingTotal().toFixed(2)),
                    });
                    onInvoiceUpdated(updatedInvoice);
                }
            }
        } catch {
            setPaymentError(t("projects.projectPayoutOverview.markPaidFailed"));
        } finally {
            setMarkingPaid(false);
        }
    };

    const togglePayment = (invoiceId: string) => {
        setExcludedPaymentIds(current => {
            const next = new Set(current);
            if (next.has(invoiceId)) next.delete(invoiceId);
            else next.add(invoiceId);
            return next;
        });
    };

    return <Card className="project-payout-overview mb-4">
        <CardBody>
            <CardHeader
                title={t("projects.projectPayoutOverview.title")}
                icon="mdi-bank-transfer-out"
                count={selectedPayableRows.length}
                actions={<div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            className="form-check-input"
                            id="project-payout-select-all"
                            type="checkbox"
                            role="switch"
                            checked={allPaymentsSelected}
                            disabled={payableRows.length === 0 || markingPaid}
                            onChange={toggleAllPayments}
                        />
                        <label className="form-check-label" htmlFor="project-payout-select-all">
                            {t("projects.projectPayoutOverview.selectAllPayments")}
                        </label>
                    </div>
                    <Button color="primary" size="sm" disabled={paymentFileBlocked || markingPaid} onClick={download}>
                        <i className="mdi mdi-download me-1" />{t("projects.projectPayoutOverview.downloadPaymentFile")}
                    </Button>
                    <Button color="success" size="sm" disabled={markPaidBlocked || markingPaid} onClick={markAsPaid}
                        aria-busy={markingPaid}>
                        {markingPaid
                            ? <Spinner size="sm" className="me-1" aria-hidden="true" />
                            : <i className="mdi mdi-check me-1" />}
                        {t("projects.projectPayoutOverview.markAsPaid")}
                    </Button>
                </div>}
            />

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3 border-bottom">
                <div>
                    <strong className="d-block">
                        {t("projects.projectPayoutOverview.weekLabel", {week: week.week, year: week.year})}
                    </strong>
                    <span>
                        {new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "long" }).format(weekStart)}
                        {" – "}
                        {new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "long", year: "numeric" }).format(weekEnd)}
                    </span>
                    <small className="text-muted d-block">
                        {t("projects.projectPayoutOverview.plannedFor", {
                            date: new Intl.DateTimeFormat(intlLocale, { weekday: "long", day: "numeric", month: "long" }).format(payoutDate),
                        })}
                    </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button color="light" size="sm" aria-label={t("projects.projectPayoutOverview.previousWeek")}
                        disabled={markingPaid}
                        onClick={() => setWeekStart(current => shiftDays(current, -7))}>
                        <i className="mdi mdi-chevron-left" />
                    </Button>
                    <Button color="light" size="sm" disabled={markingPaid}
                        onClick={() => setWeekStart(shiftDays(mondayFor(today), -7))}>
                        {t("projects.projectPayoutOverview.lastWeek")}
                    </Button>
                    <Button color="light" size="sm" aria-label={t("projects.projectPayoutOverview.nextWeek")}
                        disabled={markingPaid}
                        onClick={() => setWeekStart(current => shiftDays(current, 7))}>
                        <i className="mdi mdi-chevron-right" />
                    </Button>
                </div>
                <div className="text-end">
                    <strong className="fs-5">{currency(total)}</strong>
                    <small className="text-muted d-block">{t("projects.projectPayoutOverview.netToPay")}</small>
                </div>
            </div>

            <div className="py-3">
                {mismatchedWeekInvoices.length > 0 && <Alert color="warning" fade={false} className="mb-2 py-2">
                    <p className="mb-1">{t("projects.projectPayoutOverview.invoiceWeekMismatch", {week: week.week})}</p>
                    <ul className="mb-0">
                        {mismatchedWeekInvoices.map(invoice => <li key={invoice.id}>
                            <Link to={`/invoice/${invoice.id}`}>{invoice.name}</Link>
                        </li>)}
                    </ul>
                </Alert>}
                {multipleTenants && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-domain-off me-2" />
                    {t("projects.projectPayoutOverview.multipleTenants")}
                </Alert>}
                {!multipleTenants && selectedPayableRows.length > 0 && !tenantIbanValid &&
                    <Alert color="danger" fade={false} className="mb-2 py-2">
                        <i className="mdi mdi-alert-circle-outline me-2" />
                        {t("projects.projectPayoutOverview.tenantIbanMissing")}
                    </Alert>}
                {nonEuroRows.length > 0 && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-currency-eur-off me-2" />
                    {t("projects.projectPayoutOverview.currencyNotSupported", {count: nonEuroRows.length})}
                </Alert>}
                {pendingHours > 0 && <Alert color="warning" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-clock-alert-outline me-2" />
                    {t("projects.projectPayoutOverview.pendingHours", {count: pendingHours})}
                </Alert>}
                {workersWithoutIban.length > 0 && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-bank-off-outline me-2" />
                    {t("projects.projectPayoutOverview.workersWithoutIban", {
                        count: workersWithoutIban.length,
                        workers: workersWithoutIban.map(row => row.workerName).join(", "),
                    })}
                </Alert>}
                {workersWithInvalidIban.length > 0 && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-bank-alert me-2" />
                    {t("projects.projectPayoutOverview.workersWithInvalidIban", {
                        count: workersWithInvalidIban.length,
                        workers: workersWithInvalidIban
                            .map(row => `${row.workerName} (${normalizeIban(row.bankAccount?.iban)})`)
                            .join(", "),
                    })}
                </Alert>}
                {draftRows.length > 0 && <Alert color="warning" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-file-clock-outline me-2" />
                    {t("projects.projectPayoutOverview.draftInvoices", {count: draftRows.length})}
                </Alert>}
                {missingFeeRows.length > 0 && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-file-alert-outline me-2" />
                    {t("projects.projectPayoutOverview.missingPlatformFee", {count: missingFeeRows.length})}
                </Alert>}
                {invalidAmountRows.length > 0 && <Alert color="danger" fade={false} className="mb-2 py-2">
                    <i className="mdi mdi-cash-remove me-2" />
                    {t("projects.projectPayoutOverview.invalidNetAmount", {count: invalidAmountRows.length})}
                </Alert>}
                {!paymentFileBlocked && <Alert color="success" fade={false} className="mb-0 py-2">
                    <i className="mdi mdi-check-circle-outline me-2" />
                    {t("projects.projectPayoutOverview.ready", {count: selectedPayableRows.length})}
                </Alert>}
                {downloadError && <Alert color="danger" fade={false} className="mt-2 mb-0 py-2">{downloadError}</Alert>}
                {paymentError && <Alert color="danger" fade={false} className="mt-2 mb-0 py-2">{paymentError}</Alert>}
            </div>

            <div className="table-responsive">
                <Table hover className="project-payout-overview__table align-middle mb-0">
                    <thead><tr>
                        <th className="project-payout-overview__select" aria-label={t("projects.projectPayoutOverview.includePaymentColumn")} />
                        <th>{t("common.project")}</th>
                        <th>{t("common.worker")}</th>
                        <th>{t("projects.projectPayoutOverview.workerInvoice")}</th>
                        <th className="text-end">{t("projects.projectPayoutOverview.gross")}</th>
                        <th className="text-end">{t("projects.projectPayoutOverview.platformFee")}</th>
                        <th className="text-end">{t("projects.projectPayoutOverview.net")}</th>
                        <th>{t("common.iban")}</th>
                        <th>{t("common.status")}</th>
                    </tr></thead>
                    <tbody>
                        {displayRows.map(row => {
                            const selectable = !!row.workerInvoice && !row.alreadyPaid;
                            const included = !selectable || !excludedPaymentIds.has(row.workerInvoice!.id);
                            const ibanValid = isValidIban(row.bankAccount?.iban);
                            const feePaid = !!row.platformFeeInvoice?.IsPaid();
                            const feeUnpaidWarning = row.alreadyPaid && !feePaid;
                            const status = row.alreadyPaid
                                ? { color: feeUnpaidWarning ? "warning" : "success", text: t("common.status.paid") }
                                : !included
                                    ? { color: "light", text: t("projects.projectPayoutOverview.excluded") }
                                : !row.workerInvoice
                                    ? { color: "light", text: t("projects.projectPayoutOverview.noInvoice") }
                                    : row.pendingHours > 0
                                        ? { color: "warning", text: t("projects.projectPayoutOverview.hoursPending") }
                                        : row.hasDraftInvoice
                                            ? { color: "warning", text: t("common.status.draft") }
                                            : row.missingPlatformFeeInvoice
                                                ? { color: "danger", text: t("projects.projectPayoutOverview.feeMissing") }
                                                : !ibanValid
                                                    ? { color: "danger", text: t("projects.projectPayoutOverview.ibanMissing") }
                                                    : row.net <= 0
                                                        ? { color: "danger", text: t("projects.projectPayoutOverview.checkAmount") }
                                                        : { color: "success", text: t("projects.projectPayoutOverview.readyForPayment") };
                            return <tr key={`${row.projectId}:${row.workerId}:${row.workerInvoice?.id ?? "none"}`}
                                className={row.alreadyPaid && feePaid ? "project-payout-overview__row--paid" : undefined}>
                                <td className="project-payout-overview__select">
                                    {selectable && <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={included}
                                        disabled={markingPaid}
                                        aria-label={t("projects.projectPayoutOverview.includePayment", {
                                            payment: row.workerInvoice!.GetDisplayName() || row.workerInvoice!.id,
                                        })}
                                        onChange={() => togglePayment(row.workerInvoice!.id)}
                                    />}
                                </td>
                                <td><Link className="fw-semibold" to={`/projects/${row.projectId}`}>{row.projectName}</Link></td>
                                <td><Link to={`/accounts/${row.workerId}`}>{row.workerName}</Link></td>
                                <td>{row.workerInvoice
                                    ? <Link to={`/invoice/${row.workerInvoice.id}`}
                                        className={row.alreadyPaid ? "text-decoration-line-through" : undefined}>
                                        {row.workerInvoice.GetDisplayName() || row.workerInvoice.id}
                                    </Link>
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-end">{currency(row.gross, row.currency)}</td>
                                <td className="text-end">
                                    {row.platformFeeInvoice
                                        ? <Link to={`/invoice/${row.platformFeeInvoice.id}`}
                                            className={feePaid ? "text-decoration-line-through" : undefined}>
                                            {currency(row.platformFee, row.currency)}
                                        </Link>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-end fw-semibold">{currency(row.net, row.currency)}</td>
                                <td className={ibanValid ? "font-monospace" : "text-danger"}>
                                    {row.bankAccount?.iban || t("projects.projectPayoutOverview.notEntered")}
                                </td>
                                <td><Badge color={status.color}>
                                    {feeUnpaidWarning && <i className="mdi mdi-alert-circle me-1" role="img"
                                        aria-label={t("projects.projectPayoutOverview.workerPaidFeeUnpaid")}
                                        title={t("projects.projectPayoutOverview.workerPaidFeeUnpaid")} />}
                                    {status.text}
                                </Badge></td>
                            </tr>;
                        })}
                    </tbody>
                </Table>
            </div>
            {displayRows.length === 0 &&
                <p className="text-muted text-center mt-3 mb-0">{t("projects.projectPayoutOverview.noPayments")}</p>}
            <p className="text-muted small mt-3 mb-0">
                {t("projects.projectPayoutOverview.explanation")}
            </p>
        </CardBody>
    </Card>;
};

export default ProjectPayoutOverview;
